import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../i18n";
import { btn, Spinner } from "../lib/ui";
import { api, apiError } from "../lib/api";
import {
  ShieldCheck, User, Briefcase, ArrowRight, Check, Lock, Mail, BarChart3, FileText, Globe,
  Scissors, Car, LineChart, X, KeyRound,
} from "lucide-react";

const FEATURE_ICONS = [BarChart3, FileText, Globe];
const SECTORS = [
  { key: "peluqueria", label: "demo.sectorPeluqueria", Icon: Scissors },
  { key: "taxi", label: "demo.sectorTaxi", Icon: Car },
  { key: "consultoria", label: "demo.sectorConsultoria", Icon: LineChart },
];

export default function DemoLanding() {
  const { demoLogin } = useAuth();
  const { locale, setLocale, t } = useI18n();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const accessKey = params.get("key") || "";

  const [access, setAccess] = useState(null); // null=checking | true | false
  const [busy, setBusy] = useState(null);
  const [sector, setSector] = useState("peluqueria");
  const [leadOpen, setLeadOpen] = useState(false);
  const [lead, setLead] = useState({ name: "", email: "", message: "" });
  const [leadBusy, setLeadBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get("/demo/access", { params: { key: accessKey } })
      .then(({ data }) => { if (alive) setAccess(!!data.ok); })
      .catch(() => { if (alive) setAccess(false); });
    return () => { alive = false; };
  }, [accessKey]);

  const enter = async (profile) => {
    if (busy) return;
    setBusy(profile);
    try {
      await demoLogin(profile, sector, accessKey);
      navigate("/dashboard");
    } catch (e) {
      toast.error(apiError(e));
      setBusy(null);
    }
  };

  const sendLead = async (e) => {
    e.preventDefault();
    if (leadBusy) return;
    setLeadBusy(true);
    try {
      const { data } = await api.post("/demo/lead", { ...lead, key: accessKey });
      toast.success(data.message || t("demo.leadSent"));
      setLeadOpen(false);
      setLead({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLeadBusy(false);
    }
  };

  const inputCls = "w-full h-10 px-3 rounded-md border border-[#D5E7E5] bg-white text-sm text-[#075E62] focus:outline-none focus:ring-2 focus:ring-[#0299A0]/30";

  const Header = (
    <header className="sticky top-0 z-40 h-16 bg-[#F7FBFA]/90 backdrop-blur-md border-b border-[#D5E7E5]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center justify-between">
        <span className="text-xl font-bold tracking-tight text-[#075E62]">Lura<span className="text-[#0299A0]">.</span></span>
        <div className="flex items-center gap-3">
          <select
            data-testid="demo-language-select"
            value={locale}
            onChange={(e) => setLocale(e.target.value)}
            className="h-9 px-2 rounded-md border border-[#D5E7E5] bg-white text-sm text-[#075E62] focus:outline-none focus:ring-2 focus:ring-[#0299A0]/30"
          >
            <option value="es">ES</option><option value="en">EN</option><option value="fr">FR</option>
            <option value="pt">PT</option><option value="it">IT</option>
          </select>
          <Link to="/login" className={btn.outline}>{t("auth.login")}</Link>
        </div>
      </div>
    </header>
  );

  // Checking access
  if (access === null) {
    return <div className="min-h-screen bg-[#F7FBFA] flex items-center justify-center"><Spinner /></div>;
  }

  // Private gate — invalid / missing invitation key
  if (access === false) {
    return (
      <div className="min-h-screen bg-[#F7FBFA]">
        {Header}
        <div className="max-w-lg mx-auto px-4 sm:px-6 lg:px-8 pt-24 text-center" data-testid="demo-private-gate">
          <div className="w-14 h-14 rounded-xl bg-[#05787E]/12 border border-[#05787E]/30 flex items-center justify-center text-[#05787E] mx-auto mb-6">
            <KeyRound size={26} strokeWidth={1.75} />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#075E62]">{t("demo.privateTitle")}</h1>
          <p className="text-base text-[#5F8588] leading-relaxed mt-4">{t("demo.privateText")}</p>
          <div className="mt-8 flex justify-center gap-2 flex-wrap">
            <Link to="/register" className={btn.accent}>{t("demo.create")}</Link>
            <Link to="/login" className={btn.outline}>{t("auth.login")}</Link>
          </div>
        </div>
      </div>
    );
  }

  // Full private demo (valid key)
  return (
    <div className="min-h-screen bg-[#F7FBFA]">
      {Header}

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 sm:pt-16 pb-8">
        <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#5F8588] bg-white border border-[#D5E7E5] px-3 py-1 rounded-full mb-6">
          <ShieldCheck size={13} /> {t("demo.badge")}
        </span>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#075E62] leading-[1.05] max-w-3xl">
          {t("demo.title")}
        </h1>
        <p className="text-base text-[#5F8588] leading-relaxed mt-5 max-w-2xl">{t("demo.subtitle")}</p>
        <p className="inline-flex items-center gap-1.5 text-sm text-[#05787E] mt-4"><Lock size={14} /> {t("demo.inviteOnly")}</p>
      </section>

      {/* Sector selector */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-2">
        <p className="text-sm font-medium text-[#075E62] mb-3">{t("demo.sectorLabel")}</p>
        <div className="flex flex-wrap gap-2" data-testid="demo-sector-group">
          {SECTORS.map(({ key, label, Icon }) => {
            const active = sector === key;
            return (
              <button
                key={key}
                onClick={() => setSector(key)}
                data-testid={`demo-sector-${key}`}
                className={`inline-flex items-center gap-2 px-4 h-10 rounded-lg border text-sm font-medium transition-colors ${active ? "bg-[#05787E] border-[#05787E] text-white" : "bg-white border-[#D5E7E5] text-[#075E62] hover:border-[#0299A0]"}`}
              >
                <Icon size={16} /> {t(label)}
              </button>
            );
          })}
        </div>
      </section>

      {/* Access cards */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-4">
        <div className="grid md:grid-cols-2 gap-5">
          <div className="bg-white border border-[#D5E7E5] rounded-xl p-6 sm:p-7 flex flex-col">
            <div className="w-11 h-11 rounded-lg bg-[#0299A0]/15 border border-[#0299A0]/40 flex items-center justify-center text-[#075E62] mb-4">
              <User size={22} strokeWidth={1.75} />
            </div>
            <h2 className="text-lg font-bold text-[#075E62]">{t("demo.autonomo")}</h2>
            <p className="text-sm text-[#5F8588] leading-relaxed mt-2 flex-1">{t("demo.autonomoDesc")}</p>
            <button
              onClick={() => enter("autonomo")}
              disabled={!!busy}
              data-testid="demo-try-autonomo"
              className={btn.accent + " mt-5 w-full"}
            >
              {busy === "autonomo" ? t("demo.entering") : <>{t("demo.autonomo")} <ArrowRight size={16} /></>}
            </button>
          </div>

          <div className="bg-white border border-[#D5E7E5] rounded-xl p-6 sm:p-7 flex flex-col">
            <div className="w-11 h-11 rounded-lg bg-[#05787E]/12 border border-[#05787E]/30 flex items-center justify-center text-[#075E62] mb-4">
              <Briefcase size={22} strokeWidth={1.75} />
            </div>
            <h2 className="text-lg font-bold text-[#075E62]">{t("demo.gestor")}</h2>
            <p className="text-sm text-[#5F8588] leading-relaxed mt-2 flex-1">{t("demo.gestorDesc")}</p>
            <button
              onClick={() => enter("gestor")}
              disabled={!!busy}
              data-testid="demo-try-gestor"
              className={btn.primary + " mt-5 w-full"}
            >
              {busy === "gestor" ? t("demo.entering") : <>{t("demo.gestor")} <ArrowRight size={16} /></>}
            </button>
          </div>
        </div>

        <div data-testid="demo-notice" className="mt-5 rounded-lg border border-[#05787E]/30 bg-[#05787E]/8 px-4 py-3 text-sm text-[#075E62] flex items-start gap-2">
          <Lock size={16} className="mt-0.5 shrink-0 text-[#05787E]" />
          <span>{t("demo.notice")}</span>
        </div>
      </section>

      {/* What you can try */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <h3 className="text-sm font-bold uppercase tracking-wider text-[#5F8588] mb-5">{t("demo.what")}</h3>
        <div className="grid sm:grid-cols-3 gap-4">
          {["demo.f1", "demo.f2", "demo.f3"].map((k, idx) => {
            const Icon = FEATURE_ICONS[idx];
            return (
              <div key={k} className="bg-white border border-[#D5E7E5] rounded-lg p-5">
                <div className="w-9 h-9 rounded-md border border-[#D5E7E5] flex items-center justify-center text-[#0299A0] mb-3">
                  <Icon size={18} strokeWidth={1.75} />
                </div>
                <p className="text-sm text-[#075E62] leading-relaxed">{t(k)}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Privacy + contact */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        <div className="bg-white border border-[#D5E7E5] rounded-xl p-6 sm:p-8 grid md:grid-cols-2 gap-6 items-center">
          <div className="flex items-start gap-2 text-sm text-[#075E62]">
            <Check size={16} className="text-[#0299A0] mt-0.5 shrink-0" />
            <p className="leading-relaxed">{t("demo.privacy")}</p>
          </div>
          <div className="md:text-right">
            <p className="text-base font-medium text-[#075E62] mb-3">{t("demo.contactTitle")}</p>
            <div className="flex md:justify-end gap-2 flex-wrap">
              <button onClick={() => setLeadOpen(true)} data-testid="demo-request-access" className={btn.accent}><Mail size={16} /> {t("demo.contactBtn")}</button>
              <Link to="/login" data-testid="demo-login-link" className={btn.outline}>{t("auth.login")}</Link>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-[#D5E7E5]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-[#5F8588]">
          <span className="font-bold text-[#075E62]">Lura<span className="text-[#0299A0]">.</span></span>
          <span>{t("demo.badge")}</span>
        </div>
      </footer>

      {/* Lead modal */}
      {leadOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#075E62]/40 backdrop-blur-sm" onClick={() => setLeadOpen(false)}>
          <div className="bg-white rounded-xl border border-[#D5E7E5] w-full max-w-md p-6 shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="demo-lead-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-[#075E62]">{t("demo.leadTitle")}</h3>
              <button onClick={() => setLeadOpen(false)} data-testid="demo-lead-close" className="text-[#5F8588] hover:text-[#075E62]"><X size={18} /></button>
            </div>
            <form onSubmit={sendLead} className="space-y-3">
              <div>
                <label className="text-sm text-[#5F8588]">{t("demo.leadName")}</label>
                <input value={lead.name} onChange={(e) => setLead({ ...lead, name: e.target.value })} className={inputCls} data-testid="demo-lead-name" />
              </div>
              <div>
                <label className="text-sm text-[#5F8588]">{t("demo.leadEmail")}</label>
                <input type="email" required value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })} className={inputCls} data-testid="demo-lead-email" />
              </div>
              <div>
                <label className="text-sm text-[#5F8588]">{t("demo.leadMessage")}</label>
                <textarea rows={3} value={lead.message} onChange={(e) => setLead({ ...lead, message: e.target.value })} className={inputCls + " h-auto py-2 resize-none"} data-testid="demo-lead-message" />
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <button type="button" onClick={() => setLeadOpen(false)} className={btn.ghost}>{t("demo.leadCancel")}</button>
                <button type="submit" disabled={leadBusy} className={btn.accent} data-testid="demo-lead-submit">{leadBusy ? "…" : t("demo.leadSend")}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
